import { NetworkStatus, useQuery } from '@apollo/client';
import { ButtonDock, miniButtonDockScrollBottomPad, translateTemplateText } from '@focuslee/shared-mini-app';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EventType, GetActivityHistoryQuery, GetActivityHistoryQueryVariables } from '../client/generated/graphql';
import { getActivityHistoryQuery } from '../client/queries/activity';
import { ActivityEmptyState } from '../components/activity/ActivityEmptyState';
import { ActivityErrorState } from '../components/activity/ActivityErrorState';
import { ActivityLoadMoreButton } from '../components/activity/ActivityLoadMoreButton';
import { ActivityTimeline } from '../components/activity/ActivityTimeline';
import { ActivityPageSkeleton } from '../components/activity/skeleton/ActivityPageSkeleton';
import { Page } from '../components/Page';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { groupEventsByDate } from '../helpers/activity';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useCachedGoalsWithFallback } from '../hooks/caching/goal';
import { useCachedQuestsWithFallback } from '../hooks/caching/quest';
import { useCachedRewardsWithFallback } from '../hooks/caching/reward';

export const ActivityPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const pageSize = 20;
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const { error, data, networkStatus, fetchMore } = useQuery<GetActivityHistoryQuery, GetActivityHistoryQueryVariables>(
        getActivityHistoryQuery,
        {
            variables: { startAtHashKey: null, startAtSortKey: null, pageSize },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-first',
        },
    );

    // Handle initial hasMore state ONLY on first load
    useEffect(() => {
        if (data && !initialLoadComplete && networkStatus !== NetworkStatus.fetchMore) {
            const initialHasMore = data.eventLogs.length === pageSize;
            setHasMore(initialHasMore);
            setInitialLoadComplete(true);
        }
    }, [data, initialLoadComplete, networkStatus, pageSize]);

    const rewardsEnabled = useFeatureFlag('rewards');
    const rawEventLogs = data?.eventLogs ?? [];
    const eventLogs = useMemo(
        () => (rewardsEnabled ? rawEventLogs : rawEventLogs.filter(e => e.eventType !== EventType.RewardPurchased)),
        [rewardsEnabled, rawEventLogs],
    );

    const questIds = useMemo(() => {
        const questIdSet = new Set<string>(
            eventLogs.flatMap(log => [
                ...(log.questCompleted?.questId ? [log.questCompleted.questId] : []),
                ...(log.questMetricIncreased ? log.questMetricIncreased.map(metric => metric.questId).filter(Boolean) : []),
            ]),
        );
        return Array.from(questIdSet);
    }, [eventLogs]);

    const { categories } = useCachedCategoriesWithFallback();
    const { quests } = useCachedQuestsWithFallback(questIds);
    const { goals } = useCachedGoalsWithFallback();
    const { rewards } = useCachedRewardsWithFallback();

    const maps = useMemo(() => {
        return {
            questNameMap: Object.fromEntries(quests.map(q => [q.id, translateTemplateText(t, q.templateId, 'name', q.name)])),
            questMap: Object.fromEntries(quests.map(q => [q.id, q])),
            goalNameMap: Object.fromEntries(goals.map(g => [g.id, g.name])),
            goalMap: Object.fromEntries(goals.map(g => [g.id, g])),
            rewardNameMap: Object.fromEntries(rewards.map(r => [r.id, r.name])),
            categoryMap: Object.fromEntries(categories.map(c => [c.id, c])),
        };
    }, [quests, goals, rewards, categories, t]);
    const groupedEvents = useMemo(() => groupEventsByDate(eventLogs), [eventLogs]);

    const navigateBack = useCallback(() => navigate(-1), []);

    const handleLoadMore = useCallback(() => {
        if (rawEventLogs.length === 0 || !hasMore || networkStatus === NetworkStatus.fetchMore || isLoadingMore) {
            return;
        }

        setIsLoadingMore(true);

        const lastItem = rawEventLogs[rawEventLogs.length - 1];

        fetchMore({
            variables: {
                startAtHashKey: lastItem.id,
                startAtSortKey: lastItem.createdAt,
                pageSize,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                setIsLoadingMore(false);

                if (!fetchMoreResult || fetchMoreResult.eventLogs.length === 0) {
                    setHasMore(false);
                    return previousResult;
                }

                const newHasMore = fetchMoreResult.eventLogs.length === pageSize;
                setHasMore(newHasMore);

                const combinedEventLogs = previousResult.eventLogs.concat(fetchMoreResult.eventLogs);

                return {
                    eventLogs: combinedEventLogs,
                };
            },
        }).catch(() => {
            setIsLoadingMore(false);
        });
    }, [rawEventLogs, hasMore, networkStatus, fetchMore, pageSize, isLoadingMore]);

    // Intersection observer for auto-loading
    useEffect(() => {
        if (!hasMore || networkStatus === NetworkStatus.loading || !loadMoreRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];

                if (entry.isIntersecting && hasMore && networkStatus !== NetworkStatus.fetchMore && !isLoadingMore) {
                    handleLoadMore();
                }
            },
            {
                threshold: 0.5,
                rootMargin: '100px',
            },
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
            observer.disconnect();
        };
    }, [hasMore, networkStatus, handleLoadMore, isLoadingMore]);

    // Loading state
    if (networkStatus === NetworkStatus.loading) {
        return <ActivityPageSkeleton />;
    }

    // Error state
    if (error) {
        return (
            <Page back insetEnabled={false}>
                <ActivityErrorState error={error.message} />
            </Page>
        );
    }

    // Empty state
    if (eventLogs.length === 0) {
        return (
            <Page back insetEnabled={false}>
                <ActivityEmptyState />
            </Page>
        );
    }

    // Main timeline view
    return (
        <Page back insetEnabled={false} edgeToEdge footer={<ButtonDock main={{ text: 'Back', onClick: navigateBack, navigation: 'back' }} />}>
            <Box
                sx={{
                    height: '100%',
                    bgcolor: 'var(--tg-theme-secondary-bg-color)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    paddingTop:
                        'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px) + 12px)',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBottom: miniButtonDockScrollBottomPad,
                }}
            >
                <ActivityTimeline groupedEvents={groupedEvents} maps={maps} />

                {/* Load more section */}
                <Box ref={loadMoreRef}>
                    <ActivityLoadMoreButton
                        hasMore={hasMore}
                        isLoading={networkStatus === NetworkStatus.fetchMore || isLoadingMore}
                        onLoadMore={handleLoadMore}
                        eventLogsLength={eventLogs.length}
                    />
                </Box>
            </Box>
        </Page>
    );
};
